import { buildInitialize, convertRawBuildToBuild, deepCopy, getBaseLayer } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { BuilderState, BuildStateHistory, SnackbarOptions } from "@builder/store/slices/BuilderSlice/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { GlobalColor } from "@builder/components/general/ColorInput";
import { getCurrentLanguage } from "@builder/utils/lokalize";
import { buildSizes } from "@common/build/constants";
import { BaseLayerTypes, Build, BuildSizes, NodeStates } from "@common/build/types";
import _ from "lodash";
import { v4 } from "uuid";
import { RawBuild } from "../../../types/build.type";
import * as baseLayerReducers from "./reducers/baseLayer";
import * as nodeReducers from "./reducers/node";
import * as renderReducers from "./reducers/render";
import * as simpleCartReducers from "./reducers/simpleCart";
import * as updateNodeReducers from "./reducers/updateNode";

const initialState: BuilderState = {
    lang: getCurrentLanguage().value || "en",
    error: null,
    build: buildInitialize(),
    selectedNode: null,
    conditionalVariationMode: {
        id: null,
        index: null,
        parentDimensions: null
    },
    hoveredNode: {
        id: null,
        direction: null,
        childIndex: null
    },
    draggedNode: null,
    draggedUIComponent: null,
    currentLayer: {
        type: BaseLayerTypes.PAGES,
        index: 0
    },
    frameSize: { x: 1920, y: 1080 },
    // stateMemory: {
    //     pages: [],
    //     popups: [],
    //     cfds: []
    // },
    activeNode: null,
    snackbar: null,
    buildHistory: {
        past: [],
        future: []
    },
    nodeStates: {}
};

const builderSlice = createSlice({
    name: "Builder",
    initialState: initialState,
    reducers: {
        toggleAutoAction: (state, action: PayloadAction<string>) => {
            const currentActions = state.build.actions || [];
            if (currentActions.includes(action.payload)) {
                state.build.actions = currentActions.filter(actionId => actionId !== action.payload);
            } else {
                state.build.actions = [...currentActions, action.payload];
            }
        },
        setBuild: (state: BuilderState, action: PayloadAction<RawBuild>) => {
            const newBuild = convertRawBuildToBuild(action.payload);
            state.build = newBuild;
            const sizeKey = newBuild.size.key;
            if (sizeKey === "custom" && newBuild.size.frameSize) {
                state.frameSize = { ...newBuild.size.frameSize } as { x: number; y: number | "fit-content" };
            } else if (buildSizes[sizeKey as BuildSizes]?.value) {
                state.frameSize = buildSizes[sizeKey as BuildSizes].value as { x: number; y: number | "fit-content" };
            }
        },
        // set build that is not raw
        setCookedBuild: (state: BuilderState, action: PayloadAction<Build>) => {
            state.build = action.payload;
        },
        setLang: (state: BuilderState, action: PayloadAction<string>) => {
            state.lang = action.payload;
        },
        ...baseLayerReducers,
        ...nodeReducers,
        ...updateNodeReducers,
        ...simpleCartReducers,
        ...renderReducers,
        setFrameSize: (state, action: PayloadAction<{ x: number; y: number | "fit-content" }>) => {
            state.frameSize = action.payload;
        },
        setBuildSize: (state, action: PayloadAction<{ key: BuildSizes; frameSize?: { x: number; y: number } }>) => {
            const { key, frameSize } = action.payload;
            state.build.size = frameSize ? action.payload : { key };
            if (key === "custom") {
                if (frameSize) {
                    state.frameSize = { ...frameSize };
                }
            } else if (buildSizes[key]?.value) {
                state.frameSize = buildSizes[key].value as { x: number; y: number | "fit-content" };
            }
        },
        setActiveNode: (state, action: PayloadAction<string | null | undefined>) => {
            state.activeNode = action.payload;
        },
        // addNewImage: (state, action: PayloadAction<{ [key: string]: { id: string; name: string; url: string } }>) => {
        //     state.images.values = {
        //         ...(state.images.values ? state.images.values : {}),
        //         ...action.payload
        //     };
        // },
        addBuildGlobalColor: (state, action: PayloadAction<{ color?: string; name?: string } | undefined>) => {
            const { color, name } = action.payload || {};
            state.build.extraAttributes ||= {};
            state.build.extraAttributes.buildGlobalColors ||= [];
            const MAX_ID_LENGTH = 5; // capping length to prevent AI screwups of IDs
            let stringID = v4().slice(0, MAX_ID_LENGTH);
            while (state.build.extraAttributes.buildGlobalColors.some(color => color.id === `global-color-${stringID}`)) {
                stringID = v4().slice(0, MAX_ID_LENGTH); //making sure it's unique
            }
            const id = `global-color-${stringID}`;
            state.build.extraAttributes.buildGlobalColors.push({
                id,
                name: name || `Untitled`,
                color: color || "#fff"
            });
        },

        updateBuildGlobalColor: (state, action: PayloadAction<{ id: string; data: { name: string; color: string } }>) => {
            state.build.extraAttributes ||= {};
            state.build.extraAttributes.buildGlobalColors ||= [];
            const index = state.build.extraAttributes.buildGlobalColors.findIndex(color => color.id === action.payload.id);
            if (index >= 0) {
                state.build.extraAttributes.buildGlobalColors[index] = {
                    ...state.build.extraAttributes.buildGlobalColors[index],
                    ...action.payload.data
                };
            }
        },
        deleteBuildGlobalColor: (state, action: PayloadAction<{ key: string; replace?: string }>) => {
            const colors = state.build.extraAttributes?.buildGlobalColors;
            if (!colors) return;

            const index = colors.findIndex(color => color.id === action.payload.key);
            if (index < 0) return;

            const [deletedColor] = colors.splice(index, 1);

            const keyPattern = new RegExp(`var\\(--${action.payload.key}\\)`, "g");
            const replacement = action.payload.replace || deletedColor.color;

            state.build = _.cloneDeepWith(state.build, value => {
                if (typeof value === "string") {
                    return value.replace(keyPattern, replacement);
                }
                return undefined;
            });
        },
        
        overrideBuildGlobalColors: (state, action: PayloadAction<GlobalColor[]>) => {
            state.build.extraAttributes ||= {};
            state.build.extraAttributes.buildGlobalColors = [];

            const incoming = action.payload;

            incoming.forEach(color => {
                const idx = state.build.extraAttributes!.buildGlobalColors!.findIndex(gc => gc.id === color.id);
                if (idx >= 0) {
                    state.build.extraAttributes!.buildGlobalColors![idx] = { ...state.build.extraAttributes!.buildGlobalColors![idx], ...color };
                } else {
                    state.build.extraAttributes!.buildGlobalColors!.push({ ...color });
                }
            });
        },
        updateDecimals: (state, action: PayloadAction<number | undefined>) => {
            state.build.extraAttributes ||= {};
            state.build.extraAttributes.decimals = action.payload;
        },

        //For adding BaseLayer of global block to non permanent object outside of build
        // updateGlobalBlocks: (state, action: PayloadAction<{ id: string; gBPage: BaseLayer; name: string }>) => {
        //     if (state.globalBlocks) {
        //         state.globalBlocks[action.payload.id] = { name: action.payload.name, layer: action.payload.gBPage };
        //     } else {
        //         state.globalBlocks = {
        //             [action.payload.id]: { name: action.payload.name, layer: action.payload.gBPage }
        //         };
        //     }
        // },

        attachGlobalBlock: (state, action: PayloadAction<{ id: string; name: string }>) => {
            if (state.selectedNode) {
                const baseLayer = getBaseLayer(state);
                if (baseLayer && baseLayer.components[state.selectedNode]) {
                    const newGlobalBlocks: { [key: string]: { name: string; nodes: string[] } } = state.build.globalBlocks
                        ? deepCopy(state.build.globalBlocks)
                        : {};
                    const selectedNode = baseLayer.components[state.selectedNode];

                    selectedNode.globalBlock = action.payload.id;

                    if (newGlobalBlocks[action.payload.id]) {
                        if (!newGlobalBlocks[action.payload.id].nodes.includes(selectedNode.id)) {
                            newGlobalBlocks[action.payload.id].nodes.push(selectedNode.id);
                        }
                    } else {
                        newGlobalBlocks[action.payload.id] = { name: action.payload.name, nodes: [selectedNode.id] };
                    }

                    Object.keys(newGlobalBlocks).forEach(key => {
                        if (key !== action.payload.id) {
                            newGlobalBlocks[key].nodes = newGlobalBlocks[key].nodes.filter(id => id !== selectedNode.id);
                            if (newGlobalBlocks[key].nodes.length === 0) {
                                delete newGlobalBlocks[key];
                            }
                        }
                    });

                    state.build.globalBlocks = newGlobalBlocks;
                }
            }
        },
        detachGlobalBlock: (state, action: PayloadAction<string>) => {
            if (state.selectedNode) {
                const baseLayer = getBaseLayer(state);
                if (baseLayer && baseLayer.components[state.selectedNode]) {
                    if (state.build.globalBlocks?.[action.payload]) {
                        state.build.globalBlocks[action.payload].nodes = state.build.globalBlocks[action.payload].nodes.filter(
                            node => node !== state.selectedNode
                        );
                        if (state.build.globalBlocks[action.payload].nodes.length === 0) {
                            delete state.build.globalBlocks[action.payload];
                        }
                    }
                    const selectedNode = baseLayer.components[state.selectedNode];
                    delete selectedNode.globalBlock;
                }
            }
        },
        setSnackbar: (state, action: PayloadAction<SnackbarOptions>) => {
            state.snackbar = action.payload;
        },
        // removeAllDynamicData: (state: BuilderState, action: PayloadAction<string>) => {
        //     const baseLayer = getBaseLayer(state);
        //     if (baseLayer) {
        //         removeDynamicData(baseLayer, action.payload);
        //     }
        // }
        updateBuildHistory: (state, action: PayloadAction<BuildStateHistory>) => {
            state.buildHistory = action.payload;
        },
        undoBuildState: state => {
            if (state.buildHistory.past.length > 0) {
                const newState = state.buildHistory.past.pop();
                if (newState) {
                    if (state.buildHistory.future.length > 9) {
                        state.buildHistory.future.pop();
                    }
                    state.buildHistory.future.unshift(state.build);
                    state.build = newState;

                    if (state.build[state.currentLayer.type].length <= state.currentLayer.index) {
                        state.currentLayer = {
                            type: BaseLayerTypes.PAGES,
                            index: 0
                        };
                    }

                    if (state.selectedNode && !state.build[state.currentLayer.type][state.currentLayer.index].components[state.selectedNode]) {
                        state.selectedNode = null;
                    }
                }
            }
        },
        redoBuildState: state => {
            if (state.buildHistory.future.length > 0) {
                const newState = state.buildHistory.future.shift();
                if (newState) {
                    if (state.buildHistory.past.length > 9) {
                        state.buildHistory.past.unshift();
                    }
                    state.buildHistory.past.push(state.build);
                    state.build = newState;

                    if (state.build[state.currentLayer.type].length <= state.currentLayer.index) {
                        state.currentLayer = {
                            type: BaseLayerTypes.PAGES,
                            index: 0
                        };
                    }

                    if (state.selectedNode && !state.build[state.currentLayer.type][state.currentLayer.index].components[state.selectedNode]) {
                        state.selectedNode = null;
                    }
                }
            }
        },
        updateNodeStates: (state, action: PayloadAction<Record<string, NodeStates>>) => {
            const stateChanges = action.payload;
            Object.keys(stateChanges).forEach(nodeId => {
                if (stateChanges[nodeId] === NodeStates.DEFAULT) {
                    delete state.nodeStates[nodeId];
                } else {
                    state.nodeStates[nodeId] = stateChanges[nodeId];
                }
            });
        },
        removeBuildSettingKey: (state, action: PayloadAction<string>) => {
            Object.values(BaseLayerTypes).forEach(layer => {
                if (state.build[layer]) {
                    state.build[layer].forEach(page => {
                        Object.values(page.components).forEach(node => {
                            if (node.dynamicData === action.payload) {
                                delete node.dynamicData;
                            }
                        });
                    });
                }
            });
        },
        setConditionalVariationMode: (
            state,
            action: PayloadAction<{ id: string | null; index: number | null; parentDimensions?: { width: number; height: number } | null }>
        ) => {
            state.conditionalVariationMode = { parentDimensions: state.conditionalVariationMode?.parentDimensions, ...action.payload };
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        }
    }
});

export default builderSlice.reducer;
// export default builderSlice.reducer;
export const BuilderSliceActions = builderSlice.actions;
export const {
    toggleAutoAction,
    setBuild,
    setCookedBuild,
    addNode,
    deleteNode,
    moveNode,
    selectNode,
    setHovered,
    addLayer,
    deleteLayer,
    duplicateLayer,
    setLayerName,
    setCurrentLayer,
    setLayoutToCurrentPage,
    selectParent,
    setDraggedNode,
    setDraggedUIComponent,
    setFrameSize,
    updateDynamicDataType,
    updateCondition,
    setCondition,
    removeConditionalActions,
    updateConditionalActions,
    clearBorderStyles,
    updateBuildActions,
    updateReceiptStyles,
    updateReceiptSettings,
    updateCashLedgerSettings,
    updateEOSSettings,
    updateActionBuilder,
    deleteActionBuilder,
    duplicateActionBuilder,
    updateNodeActions,
    setBuildSize,
    setActiveNode,
    copyNode,
    pasteNodeFromJSON,
    mergeJSXIntoNode,
    updateNodeProperty,
    updateNodePropertyByNodeIdAndLayer,
    // updateGlobalBlocks,
    attachGlobalBlock,
    addFontToAttributes,
    setFontRoots,
    batchEditNodes,
    updateNodeQuery,
    addBuildGlobalColor,
    updateBuildGlobalColor,
    updateDecimals,
    deleteBuildGlobalColor,
    overrideBuildGlobalColors,
    setLang,
    copyTemplateNode,
    setSnackbar,
    setSpecialLayers,
    removeSpecialLayer,
    copyDesignSetLayer,
    installDesignSet,
    updateChildrenSize,
    detachGlobalBlock,
    undoBuildState,
    redoBuildState,
    updateNodeStates,
    updatePaymentGridSettings,
    removeBuildSettingKey,
    setConditionalVariationMode,
    setError
    // removeAllDynamicData
} = builderSlice.actions;
